import {
  withDefault,
  encodeString,
  decodeEnum,
  decodeString,
  encodeArray,
  decodeArray,
} from 'use-query-params';

import {
  timeRangeOptions,
  type Range,
  type TimeRangeOption,
} from './RangePicker.tsx';
import { groupByOptions, type GroupBy } from './config.ts';
import type { FilterData } from './types.ts';

const allGroupByOptions = [
  'None',
  ...groupByOptions.slice(),
] satisfies GroupBy[];

export const GroupByParam = withDefault<GroupBy, GroupBy>(
  {
    encode: (value) => encodeString(value),
    decode: (value) => decodeEnum(value, allGroupByOptions) ?? 'Status',
  },
  'Status',
);

export const RangeParam = withDefault<Range, Range>(
  {
    encode: (value) =>
      encodeString(
        value.option === 'custom'
          ? value.bounds.map((b) => b.toISOString()).join('_')
          : value.option,
      ),
    decode: (value) => {
      const decoded = decodeString(value);
      if (decoded?.includes('_')) {
        const [start, end] = decoded.split('_').map((d) => new Date(d));

        if (
          start.toString() !== 'Invalid Date' &&
          end.toString() !== 'Invalid Date'
        ) {
          return { option: 'custom', bounds: [start, end] };
        }
      }

      if (timeRangeOptions.includes(decoded as never)) {
        return { option: decoded as TimeRangeOption };
      }

      return { option: '24 hours' };
    },
  },
  { option: '24 hours' },
);

export const FiltersParam = withDefault<FilterData[], FilterData[]>(
  {
    encode: (value) => encodeArray(value.map((v) => JSON.stringify(v))),
    decode: (value) =>
      // TODO: This is a bit lousy, we'll probably get crashes if someone fiddles with the URL
      (decodeArray(value) ?? []).map((v) => JSON.parse(v as string)),
  } as const,
  [],
);

export const FrozenBoundsParam = withDefault<
  [Date, Date] | undefined,
  [Date, Date] | undefined
>(
  {
    encode: (value) =>
      value ? `${value[0].toISOString()}_${value[1].toISOString()}` : undefined,
    decode: (value) => {
      const decoded = decodeString(value);

      if (decoded?.includes('_')) {
        const [start, end] = decoded.split('_').map((d) => new Date(d));

        if (
          start.toString() !== 'Invalid Date' &&
          end.toString() !== 'Invalid Date'
        ) {
          return [start, end];
        }
      }
    },
  },
  undefined,
);
